import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cva } from 'class-variance-authority';
import { createContext, use, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../../Icon/Icon';
import { inputBase, type InputBaseProps } from '../base';

const comboboxTriggerStyles = cva('flex items-center');

const comboboxInputStyles = cva('pr-xl');

const comboboxContentStyles = cva(
  'group/combobox-content max-h-[266px] w-[var(--anchor-width)] overflow-y-auto rounded-s bg-surface-strong shadow-l',
);

const comboboxListStyles = cva('p-xxs data-[empty]:p-0');

const comboboxItemStyles = cva(
  'relative flex cursor-pointer items-center rounded-s p-xs pr-xl text-body-l text-content data-[highlighted]:bg-primary data-[highlighted]:text-content-inverse',
);

const comboboxEmptyStyles = cva(
  'hidden p-s text-center text-content-subdued group-data-[empty]/combobox-content:block',
);

type ComboboxContextValue = {
  id: string;
};

const ComboboxContext = createContext<ComboboxContextValue | null>(null);

const useComboboxContext = () => {
  const context = use(ComboboxContext);
  if (!context) {
    throw new Error(
      'Combobox parts must be rendered inside a parent <Combobox.Root>.',
    );
  }
  return context;
};

/**
 * Searchable single- or multi-select input, built on Base UI's `Combobox` primitive.
 *
 * Selected values in multi-select mode render as our own `Chip` in a separate row below
 * the field — see the `@example` below.
 *
 * @example
 * <Combobox.Root id={id} items={countries} value={value} onValueChange={setValue}>
 *   <Combobox.Input placeholder="Select a country" />
 *   <Combobox.Content>
 *     <Combobox.Empty>No countries found.</Combobox.Empty>
 *     <Combobox.List>
 *       {(country) => (
 *         <Combobox.Item key={country.value} value={country}>
 *           {country.label}
 *         </Combobox.Item>
 *       )}
 *     </Combobox.List>
 *   </Combobox.Content>
 * </Combobox.Root>
 *
 * @example Multi-select with chips
 * <Combobox.Root id={id} items={countries} multiple value={value} onValueChange={setValue}>
 *   <Combobox.Input placeholder="Add country" />
 *   <Combobox.Content>
 *     <Combobox.Empty>No countries found.</Combobox.Empty>
 *     <Combobox.List>
 *       {(country) => (
 *         <Combobox.Item key={country.value} value={country}>
 *           {country.label}
 *         </Combobox.Item>
 *       )}
 *     </Combobox.List>
 *   </Combobox.Content>
 * </Combobox.Root>
 * <div className="flex flex-wrap gap-xs pt-xs">
 *   {value.map((country) => (
 *     <Chip
 *       key={country.value}
 *       pattern="deletable"
 *       text={country.label}
 *       selected
 *       onDelete={() => setValue(value.filter((v) => v.value !== country.value))}
 *     />
 *   ))}
 * </div>
 */
function Combobox<
  Value,
  Multiple extends boolean | undefined = false,
  Item = Value,
>({
  id: idProp,
  multiple,
  ...props
}: ComboboxPrimitive.Root.Props<Value, Multiple, Item> & { id?: string }) {
  const generatedId = useId();
  const id = idProp || generatedId;

  return (
    <ComboboxContext value={{ id }}>
      <ComboboxPrimitive.Root multiple={multiple} {...props} />
    </ComboboxContext>
  );
}

const ComboboxValue = ComboboxPrimitive.Value;

const ComboboxTrigger = (
  props: Omit<ComboboxPrimitive.Trigger.Props, 'className'>,
) => (
  <ComboboxPrimitive.Trigger className={comboboxTriggerStyles()} {...props}>
    <Icon name="expand_more" size="large" />
  </ComboboxPrimitive.Trigger>
);

const ComboboxInput = ({
  intent,
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'className' | 'id'> & {
  intent?: InputBaseProps['intent'];
}) => {
  const { id } = useComboboxContext();

  return (
    <div className="relative">
      <ComboboxPrimitive.Input
        id={id}
        className={twMerge(inputBase({ intent }), comboboxInputStyles())}
        {...props}
      />
      <div className="absolute inset-y-0 right-0 flex items-center px-xs">
        <ComboboxTrigger />
      </div>
    </div>
  );
};

const ComboboxContent = (
  props: Omit<ComboboxPrimitive.Popup.Props, 'className'>,
) => (
  <ComboboxPrimitive.Portal>
    <ComboboxPrimitive.Positioner sideOffset={5} className="z-50">
      <ComboboxPrimitive.Popup className={comboboxContentStyles()} {...props} />
    </ComboboxPrimitive.Positioner>
  </ComboboxPrimitive.Portal>
);

const ComboboxList = (
  props: Omit<ComboboxPrimitive.List.Props, 'className'>,
) => <ComboboxPrimitive.List className={comboboxListStyles()} {...props} />;

const ComboboxItem = ({
  children,
  ...props
}: Omit<ComboboxPrimitive.Item.Props, 'className'>) => (
  <ComboboxPrimitive.Item className={comboboxItemStyles()} {...props}>
    {children}
    <ComboboxPrimitive.ItemIndicator className="absolute right-xs flex items-center">
      <Icon name="check" size="small" />
    </ComboboxPrimitive.ItemIndicator>
  </ComboboxPrimitive.Item>
);

const ComboboxEmpty = (
  props: Omit<ComboboxPrimitive.Empty.Props, 'className'>,
) => <ComboboxPrimitive.Empty className={comboboxEmptyStyles()} {...props} />;

export {
  ComboboxContent as Content,
  ComboboxEmpty as Empty,
  ComboboxInput as Input,
  ComboboxItem as Item,
  ComboboxList as List,
  Combobox as Root,
  ComboboxTrigger as Trigger,
  ComboboxValue as Value,
};
